// Permission definitions - Backend API key formatı: MODULE.ACTION
// Backend'den gelen tüm key'lerle birebir eşleşmeli

export interface Permission {
  admin: string | readonly string[];
  staff?: string | readonly string[];
}

/** API permission key'leri - backend ile birebir aynı */
export const PERMISSION_KEYS = {
  // ── Page (module master switch) — gating both sidebar + backend access ──
  ANNOUNCEMENTS_PAGE: "ANNOUNCEMENTS.PAGE",
  BANNED_USERS_PAGE: "BANNED_USERS.PAGE",
  EDUCATION_CATEGORIES_PAGE: "EDUCATION_CATEGORIES.PAGE",
  EDUCATIONS_PAGE: "EDUCATIONS.PAGE",
  EXAMS_PAGE: "EXAMS.PAGE",
  SURVEYS_PAGE: "SURVEYS.PAGE",
  FILES_PAGE: "FILES.PAGE",
  LIBRARY_PAGE: "LIBRARY.PAGE",
  USERS_PAGE: "USERS.PAGE",
  INTERNS_PAGE: "INTERNS.PAGE",
  ROLES_PAGE: "ROLES.PAGE",
  SUPPORT_TICKETS_PAGE: "SUPPORT_TICKETS.PAGE",
  AUDIT_LOGS_PAGE: "AUDIT_LOGS.PAGE",
  REPORTS_PAGE: "REPORTS.PAGE",
  DEPARTMENTS_PAGE: "DEPARTMENTS.PAGE",
  LOCATIONS_PAGE: "LOCATIONS.PAGE",
  EMAIL_BROADCAST_PAGE: "EMAIL_BROADCAST.PAGE",
  SMS_BROADCAST_PAGE: "SMS_BROADCAST.PAGE",
  COMPANIES_PAGE: "COMPANIES.PAGE",
  DOMAIN_SETTINGS_PAGE: "DOMAIN_SETTINGS.PAGE",
  CERTIFICATES_PAGE: "CERTIFICATES.PAGE",
  USER_GROUPS_PAGE: "USER_GROUPS.PAGE",

  ANNOUNCEMENTS_VIEW: "ANNOUNCEMENTS.VIEW",
  ANNOUNCEMENTS_CREATE: "ANNOUNCEMENTS.CREATE",
  ANNOUNCEMENTS_UPDATE: "ANNOUNCEMENTS.UPDATE",
  ANNOUNCEMENTS_DELETE: "ANNOUNCEMENTS.DELETE",
  BANNED_USERS_VIEW: "BANNED_USERS.VIEW",
  BANNED_USERS_CREATE: "BANNED_USERS.CREATE",
  BANNED_USERS_UPDATE: "BANNED_USERS.UPDATE",
  BANNED_USERS_DELETE: "BANNED_USERS.DELETE",
  EDUCATION_CATEGORIES_VIEW: "EDUCATION_CATEGORIES.VIEW",
  EDUCATION_CATEGORIES_CREATE: "EDUCATION_CATEGORIES.CREATE",
  EDUCATION_CATEGORIES_UPDATE: "EDUCATION_CATEGORIES.UPDATE",
  EDUCATION_CATEGORIES_DELETE: "EDUCATION_CATEGORIES.DELETE",
  EDUCATIONS_VIEW: "EDUCATIONS.VIEW",
  EDUCATIONS_CREATE: "EDUCATIONS.CREATE",
  EDUCATIONS_UPDATE: "EDUCATIONS.UPDATE",
  EDUCATIONS_DELETE: "EDUCATIONS.DELETE",
  EDUCATIONS_APPROVE: "EDUCATIONS.APPROVE",
  /** Eğitim havuzuna kişi ekleme/çıkarma — USERS.CREATE'den ayrı,
   *  kullanıcı oluşturma yetkisi olmayanlar da bu yetkiyle havuza ekleyebilir. */
  EDUCATIONS_ENROLL: "EDUCATIONS.ENROLL",
  EXAMS_VIEW: "EXAMS.VIEW",
  EXAMS_CREATE: "EXAMS.CREATE",
  EXAMS_UPDATE: "EXAMS.UPDATE",
  EXAMS_DELETE: "EXAMS.DELETE",
  SURVEYS_VIEW: "SURVEYS.VIEW",
  SURVEYS_CREATE: "SURVEYS.CREATE",
  SURVEYS_UPDATE: "SURVEYS.UPDATE",
  SURVEYS_DELETE: "SURVEYS.DELETE",
  FILES_VIEW: "FILES.VIEW",
  FILES_CREATE: "FILES.CREATE",
  FILES_UPDATE: "FILES.UPDATE",
  FILES_DELETE: "FILES.DELETE",
  LIBRARY_VIEW: "LIBRARY.VIEW",
  LIBRARY_CREATE: "LIBRARY.CREATE",
  LIBRARY_UPDATE: "LIBRARY.UPDATE",
  LIBRARY_DELETE: "LIBRARY.DELETE",
  USERS_VIEW: "USERS.VIEW",
  USERS_VIEW_ALL: "USERS.VIEW_ALL",
  USERS_CREATE: "USERS.CREATE",
  USERS_UPDATE: "USERS.UPDATE",
  USERS_UPDATE_PASSWORD: "USERS.UPDATE_PASSWORD",
  USERS_DELETE: "USERS.DELETE",
  INTERNS_VIEW: "INTERNS.VIEW",
  INTERNS_CREATE: "INTERNS.CREATE",
  INTERNS_UPDATE: "INTERNS.UPDATE",
  INTERNS_DELETE: "INTERNS.DELETE",
  ROLES_VIEW: "ROLES.VIEW",
  ROLES_CREATE: "ROLES.CREATE",
  ROLES_UPDATE: "ROLES.UPDATE",
  ROLES_DELETE: "ROLES.DELETE",
  ROLES_ASSIGN: "ROLES.ASSIGN",
  ROLES_REVOKE: "ROLES.REVOKE",
  SUPPORT_TICKETS_VIEW: "SUPPORT_TICKETS.VIEW",
  SUPPORT_TICKETS_CREATE: "SUPPORT_TICKETS.CREATE",
  SUPPORT_TICKETS_UPDATE: "SUPPORT_TICKETS.UPDATE",
  SUPPORT_TICKETS_DELETE: "SUPPORT_TICKETS.DELETE",
  AUDIT_LOGS_VIEW: "AUDIT_LOGS.VIEW",
  REPORTS_VIEW: "REPORTS.VIEW",
  DEPARTMENTS_VIEW: "DEPARTMENTS.VIEW",
  DEPARTMENTS_CREATE: "DEPARTMENTS.CREATE",
  DEPARTMENTS_UPDATE: "DEPARTMENTS.UPDATE",
  DEPARTMENTS_DELETE: "DEPARTMENTS.DELETE",
  LOCATIONS_VIEW: "LOCATIONS.VIEW",
  LOCATIONS_CREATE: "LOCATIONS.CREATE",
  LOCATIONS_UPDATE: "LOCATIONS.UPDATE",
  LOCATIONS_DELETE: "LOCATIONS.DELETE",
  EMAIL_BROADCAST_VIEW: "EMAIL_BROADCAST.VIEW",
  EMAIL_BROADCAST_SEND: "EMAIL_BROADCAST.SEND",
  SMS_BROADCAST_VIEW: "SMS_BROADCAST.VIEW",
  SMS_BROADCAST_SEND: "SMS_BROADCAST.SEND",
  COMPANIES_VIEW: "COMPANIES.VIEW",
  COMPANIES_CREATE: "COMPANIES.CREATE",
  COMPANIES_UPDATE: "COMPANIES.UPDATE",
  COMPANIES_DELETE: "COMPANIES.DELETE",
  // Sistem Ayarları — sertifika numarası pattern'i vb. (Sadece VIEW + UPDATE)
  DOMAIN_SETTINGS_VIEW: "DOMAIN_SETTINGS.VIEW",
  DASHBOARD_EDUCATION_MAP_VIEW: "DASHBOARD.EDUCATION_MAP_VIEW",
  DOMAIN_SETTINGS_UPDATE: "DOMAIN_SETTINGS.UPDATE",
  // Sertifikalar — bağımsız backend modülü (CERTIFICATES.*)
  CERTIFICATES_VIEW: "CERTIFICATES.VIEW",
  CERTIFICATES_CREATE: "CERTIFICATES.CREATE",
  CERTIFICATES_UPDATE: "CERTIFICATES.UPDATE",
  CERTIFICATES_DELETE: "CERTIFICATES.DELETE",
  CERTIFICATES_ASSIGN: "CERTIFICATES.ASSIGN",
  CERTIFICATES_TEMPLATES_VIEW: "CERTIFICATES.TEMPLATES_VIEW",
  CERTIFICATES_TEMPLATES_CREATE: "CERTIFICATES.TEMPLATES_CREATE",
  CERTIFICATES_TEMPLATES_UPDATE: "CERTIFICATES.TEMPLATES_UPDATE",
  CERTIFICATES_TEMPLATES_DELETE: "CERTIFICATES.TEMPLATES_DELETE",
  USER_GROUPS_VIEW: "USER_GROUPS.VIEW",
  USER_GROUPS_CREATE: "USER_GROUPS.CREATE",
  USER_GROUPS_UPDATE: "USER_GROUPS.UPDATE",
  USER_GROUPS_DELETE: "USER_GROUPS.DELETE",
} as const;

/** Sayfa/aksiyon bazlı permission alias'ları - backend key'lerine map.
 *  Menü görünürlüğü ve route guard'ı PAGE yetkisine bakar (modül master
 *  anahtarı — yoksa hem sol menüde görünmez hem backend 403 atar).
 *  Aksiyonlar (oluşturma/güncelleme/silme/onaylama) ayrı CRUD yetkileriyle
 *  kontrol edilir. */
export const PERMISSIONS = {
  DASHBOARD: {
    admin: PERMISSION_KEYS.ANNOUNCEMENTS_VIEW,
    staff: PERMISSION_KEYS.ANNOUNCEMENTS_VIEW,
  },
  DASHBOARD_EDUCATION_MAP_VIEW: {
    admin: PERMISSION_KEYS.DASHBOARD_EDUCATION_MAP_VIEW,
    staff: PERMISSION_KEYS.DASHBOARD_EDUCATION_MAP_VIEW,
  },


  // ── Page aliases — used by menu.ts + route-map.ts ──
  ANNOUNCEMENTS_PAGE: { admin: PERMISSION_KEYS.ANNOUNCEMENTS_PAGE },
  BLACK_LIST_PAGE: { admin: PERMISSION_KEYS.BANNED_USERS_PAGE },
  CATEGORIES_PAGE: { admin: PERMISSION_KEYS.EDUCATION_CATEGORIES_PAGE },
  EDUCATION_PAGE: { admin: PERMISSION_KEYS.EDUCATIONS_PAGE },
  EXAMS_PAGE: { admin: PERMISSION_KEYS.EXAMS_PAGE },
  POLLS_PAGE: { admin: PERMISSION_KEYS.SURVEYS_PAGE },
  ITEMS_PAGE: { admin: PERMISSION_KEYS.FILES_PAGE },
  LIBRARY_PAGE: {
    admin: PERMISSION_KEYS.LIBRARY_PAGE,
    staff: PERMISSION_KEYS.LIBRARY_PAGE,
  },
  STAFF_PAGE: { admin: PERMISSION_KEYS.USERS_PAGE },
  INTERN_PAGE: { admin: PERMISSION_KEYS.INTERNS_PAGE },
  ROLES_AND_PERMISSIONS_PAGE: { admin: PERMISSION_KEYS.ROLES_PAGE },
  SUPPORT_TICKETS_PAGE: {
    admin: PERMISSION_KEYS.SUPPORT_TICKETS_PAGE,
    staff: PERMISSION_KEYS.SUPPORT_TICKETS_PAGE,
  },
  LOGS_PAGE: { admin: PERMISSION_KEYS.AUDIT_LOGS_PAGE },
  REPORTS_PAGE: { admin: PERMISSION_KEYS.REPORTS_PAGE },
  DEPARTMENTS_PAGE: { admin: PERMISSION_KEYS.DEPARTMENTS_PAGE },
  LOCATIONS_PAGE: { admin: PERMISSION_KEYS.LOCATIONS_PAGE },
  EMAIL_BROADCAST_PAGE: { admin: PERMISSION_KEYS.EMAIL_BROADCAST_PAGE },
  SMS_BROADCAST_PAGE: { admin: PERMISSION_KEYS.SMS_BROADCAST_PAGE },
  COMPANIES_PAGE: { admin: PERMISSION_KEYS.COMPANIES_PAGE },
  DOMAIN_SETTINGS_PAGE: { admin: PERMISSION_KEYS.DOMAIN_SETTINGS_PAGE },
  // Sertifikalar — bağımsız backend modülü
  CERTIFICATES_PAGE: { admin: PERMISSION_KEYS.CERTIFICATES_PAGE },
  USER_GROUPS_PAGE: { admin: PERMISSION_KEYS.USER_GROUPS_PAGE },
  // "Eğitim Onay" sayfası — EDUCATIONS modülü altında
  REQUEST_AND_APPROVAL_PAGE: { admin: PERMISSION_KEYS.EDUCATIONS_PAGE },

  ANNOUNCEMENTS_VIEW: {
    admin: PERMISSION_KEYS.ANNOUNCEMENTS_VIEW,
    staff: PERMISSION_KEYS.ANNOUNCEMENTS_VIEW,
  },
  ANNOUNCEMENTS_CREATE: { admin: PERMISSION_KEYS.ANNOUNCEMENTS_CREATE },
  ANNOUNCEMENTS_UPDATE: { admin: PERMISSION_KEYS.ANNOUNCEMENTS_UPDATE },
  ANNOUNCEMENTS_DELETE: { admin: PERMISSION_KEYS.ANNOUNCEMENTS_DELETE },
  // Kara Liste -> BANNED_USERS
  BLACK_LIST_VIEW: { admin: PERMISSION_KEYS.BANNED_USERS_VIEW },
  BLACK_LIST_CREATE: { admin: PERMISSION_KEYS.BANNED_USERS_CREATE },
  BLACK_LIST_UPDATE: { admin: PERMISSION_KEYS.BANNED_USERS_UPDATE },
  BLACK_LIST_DELETE: { admin: PERMISSION_KEYS.BANNED_USERS_DELETE },
  // Kategoriler -> EDUCATION_CATEGORIES
  CATEGORIES_VIEW: { admin: PERMISSION_KEYS.EDUCATION_CATEGORIES_VIEW },
  CATEGORIES_CREATE: { admin: PERMISSION_KEYS.EDUCATION_CATEGORIES_CREATE },
  CATEGORIES_UPDATE: { admin: PERMISSION_KEYS.EDUCATION_CATEGORIES_UPDATE },
  CATEGORIES_DELETE: { admin: PERMISSION_KEYS.EDUCATION_CATEGORIES_DELETE },
  // Eğitim Yönetimi -> EDUCATIONS
  // VIEW: hem admin (Eğitim Yönetimi) hem kursiyer (Eğitim Kataloğu, Eğitimlerim, Taleplerim, Sertifikalarım) için.
  EDUCATION_VIEW: {
    admin: PERMISSION_KEYS.EDUCATIONS_VIEW,
    staff: PERMISSION_KEYS.EDUCATIONS_VIEW,
  },
  // CREATE: kursiyer için "eğitim talebi gönderme" akışında kullanılır.
  EDUCATION_CREATE: {
    admin: PERMISSION_KEYS.EDUCATIONS_CREATE,
    staff: PERMISSION_KEYS.EDUCATIONS_CREATE,
  },
  EDUCATION_UPDATE: { admin: PERMISSION_KEYS.EDUCATIONS_UPDATE },
  EDUCATION_DELETE: { admin: PERMISSION_KEYS.EDUCATIONS_DELETE },
  EDUCATION_APPROVE: { admin: PERMISSION_KEYS.EDUCATIONS_APPROVE },
  // EXAMS.VIEW: Sınavlarım sayfası için kursiyere açık.
  EXAMS_VIEW: {
    admin: PERMISSION_KEYS.EXAMS_VIEW,
    staff: PERMISSION_KEYS.EXAMS_VIEW,
  },
  EXAMS_CREATE: { admin: PERMISSION_KEYS.EXAMS_CREATE },
  EXAMS_UPDATE: { admin: PERMISSION_KEYS.EXAMS_UPDATE },
  EXAMS_DELETE: { admin: PERMISSION_KEYS.EXAMS_DELETE },
  // Anketler -> SURVEYS — Anketlerim sayfası için kursiyere açık.
  POLLS_VIEW: {
    admin: PERMISSION_KEYS.SURVEYS_VIEW,
    staff: PERMISSION_KEYS.SURVEYS_VIEW,
  },
  POLLS_CREATE: { admin: PERMISSION_KEYS.SURVEYS_CREATE },
  POLLS_UPDATE: { admin: PERMISSION_KEYS.SURVEYS_UPDATE },
  POLLS_DELETE: { admin: PERMISSION_KEYS.SURVEYS_DELETE },
  // Items -> FILES
  ITEMS_VIEW: { admin: PERMISSION_KEYS.FILES_VIEW },
  ITEMS_CREATE: { admin: PERMISSION_KEYS.FILES_CREATE },
  ITEMS_UPDATE: { admin: PERMISSION_KEYS.FILES_UPDATE },
  ITEMS_DELETE: { admin: PERMISSION_KEYS.FILES_DELETE },
  // Kütüphane — kursiyer EÜAŞ Kütüphane sayfasına erişebilir.
  LIBRARY_VIEW: {
    admin: PERMISSION_KEYS.LIBRARY_VIEW,
    staff: PERMISSION_KEYS.LIBRARY_VIEW,
  },
  LIBRARY_CREATE: { admin: PERMISSION_KEYS.LIBRARY_CREATE },
  LIBRARY_UPDATE: { admin: PERMISSION_KEYS.LIBRARY_UPDATE },
  LIBRARY_DELETE: { admin: PERMISSION_KEYS.LIBRARY_DELETE },
  // Stajyer -> INTERNS (backend ayrı namespace kullanıyor)
  INTERN_VIEW: { admin: PERMISSION_KEYS.INTERNS_VIEW },
  INTERN_CREATE: { admin: PERMISSION_KEYS.INTERNS_CREATE },
  INTERN_UPDATE: { admin: PERMISSION_KEYS.INTERNS_UPDATE },
  INTERN_DELETE: { admin: PERMISSION_KEYS.INTERNS_DELETE },
  // Personel -> USERS
  STAFF_VIEW: { admin: PERMISSION_KEYS.USERS_VIEW },
  // Tüm personeli görüntüleme — departman/başkanlık kısıtını atlatır.
  STAFF_VIEW_ALL: { admin: PERMISSION_KEYS.USERS_VIEW_ALL },
  STAFF_CREATE: { admin: PERMISSION_KEYS.USERS_CREATE },
  STAFF_UPDATE: { admin: PERMISSION_KEYS.USERS_UPDATE },
  STAFF_UPDATE_PASSWORD: { admin: PERMISSION_KEYS.USERS_UPDATE_PASSWORD },
  STAFF_DELETE: { admin: PERMISSION_KEYS.USERS_DELETE },
  // Roller -> ROLES
  // Kişiye rol ata / rol kaldır — Personel sayfasındaki "Rol Ata" aksiyonu.
  STAFF_ASSIGN_ROLE: { admin: PERMISSION_KEYS.ROLES_ASSIGN },
  STAFF_REVOKE_ROLE: { admin: PERMISSION_KEYS.ROLES_REVOKE },
  ROLES_AND_PERMISSIONS_VIEW: { admin: PERMISSION_KEYS.ROLES_VIEW },
  ROLES_AND_PERMISSIONS_CREATE: { admin: PERMISSION_KEYS.ROLES_CREATE },
  ROLES_AND_PERMISSIONS_UPDATE: { admin: PERMISSION_KEYS.ROLES_UPDATE },
  ROLES_AND_PERMISSIONS_DELETE: { admin: PERMISSION_KEYS.ROLES_DELETE },
  // Sertifikalar — bağımsız CERTIFICATES.* modülü.
  // VIEW: admin Sertifikalar sayfası + kursiyer "Sertifikalarım" sayfası (staff için
  // backend, kullanıcının kendi sertifikalarını CERTIFICATES.VIEW olmaksızın dönen
  // ayrı bir endpoint kullanır — burada staff yetkisini sayfa görünürlüğü için
  // CERTIFICATES.PAGE'e bağlıyoruz, admin tarafı CERTIFICATES.VIEW okur).
  CERTIFICATES_VIEW: {
    admin: PERMISSION_KEYS.CERTIFICATES_VIEW,
    staff: PERMISSION_KEYS.CERTIFICATES_PAGE,
  },
  CERTIFICATES_CREATE: { admin: PERMISSION_KEYS.CERTIFICATES_CREATE },
  CERTIFICATES_UPDATE: { admin: PERMISSION_KEYS.CERTIFICATES_UPDATE },
  CERTIFICATES_DELETE: { admin: PERMISSION_KEYS.CERTIFICATES_DELETE },
  CERTIFICATES_ASSIGN: { admin: PERMISSION_KEYS.CERTIFICATES_ASSIGN },
  CERTIFICATES_TEMPLATES_VIEW: { admin: PERMISSION_KEYS.CERTIFICATES_TEMPLATES_VIEW },
  CERTIFICATES_TEMPLATES_CREATE: { admin: PERMISSION_KEYS.CERTIFICATES_TEMPLATES_CREATE },
  CERTIFICATES_TEMPLATES_UPDATE: { admin: PERMISSION_KEYS.CERTIFICATES_TEMPLATES_UPDATE },
  CERTIFICATES_TEMPLATES_DELETE: { admin: PERMISSION_KEYS.CERTIFICATES_TEMPLATES_DELETE },
  USER_GROUPS_VIEW: { admin: PERMISSION_KEYS.USER_GROUPS_VIEW },
  USER_GROUPS_CREATE: { admin: PERMISSION_KEYS.USER_GROUPS_CREATE },
  USER_GROUPS_UPDATE: { admin: PERMISSION_KEYS.USER_GROUPS_UPDATE },
  USER_GROUPS_DELETE: { admin: PERMISSION_KEYS.USER_GROUPS_DELETE },
  // Destek Taleplerim — kursiyer hem görüntüler hem oluşturur.
  SUPPORT_TICKETS_VIEW: {
    admin: PERMISSION_KEYS.SUPPORT_TICKETS_VIEW,
    staff: PERMISSION_KEYS.SUPPORT_TICKETS_VIEW,
  },
  SUPPORT_TICKETS_CREATE: {
    admin: PERMISSION_KEYS.SUPPORT_TICKETS_CREATE,
    staff: PERMISSION_KEYS.SUPPORT_TICKETS_CREATE,
  },
  SUPPORT_TICKETS_UPDATE: { admin: PERMISSION_KEYS.SUPPORT_TICKETS_UPDATE },
  SUPPORT_TICKETS_DELETE: { admin: PERMISSION_KEYS.SUPPORT_TICKETS_DELETE },
  // Eğitim Onay sayfası
  REQUEST_AND_APPROVAL_VIEW: { admin: PERMISSION_KEYS.EDUCATIONS_VIEW },
  // "Kişi Ekle" butonu artık EDUCATIONS.ENROLL'e bağlı (USERS.CREATE değil) —
  // bazı rollerin kullanıcı oluşturma yetkisi olmasa bile havuza ekleyebilmeli.
  REQUEST_AND_APPROVAL_CREATE: { admin: PERMISSION_KEYS.EDUCATIONS_ENROLL },
  REQUEST_AND_APPROVAL_UPDATE: { admin: PERMISSION_KEYS.USERS_UPDATE },
  REQUEST_AND_APPROVAL_DELETE: { admin: PERMISSION_KEYS.USERS_DELETE },
  // Loglar -> AUDIT_LOGS
  LOGS_VIEW: { admin: PERMISSION_KEYS.AUDIT_LOGS_VIEW },
  // Raporlar -> REPORTS (sadece görüntüleme)
  REPORTS_VIEW: { admin: PERMISSION_KEYS.REPORTS_VIEW },
  // Birimler
  DEPARTMENTS_VIEW: { admin: PERMISSION_KEYS.DEPARTMENTS_VIEW },
  DEPARTMENTS_CREATE: { admin: PERMISSION_KEYS.DEPARTMENTS_CREATE },
  DEPARTMENTS_UPDATE: { admin: PERMISSION_KEYS.DEPARTMENTS_UPDATE },
  DEPARTMENTS_DELETE: { admin: PERMISSION_KEYS.DEPARTMENTS_DELETE },
  LOCATIONS_VIEW: { admin: PERMISSION_KEYS.LOCATIONS_VIEW },
  LOCATIONS_CREATE: { admin: PERMISSION_KEYS.LOCATIONS_CREATE },
  LOCATIONS_UPDATE: { admin: PERMISSION_KEYS.LOCATIONS_UPDATE },
  LOCATIONS_DELETE: { admin: PERMISSION_KEYS.LOCATIONS_DELETE },
  EMAIL_BROADCAST_VIEW: { admin: PERMISSION_KEYS.EMAIL_BROADCAST_VIEW },
  EMAIL_BROADCAST_SEND: { admin: PERMISSION_KEYS.EMAIL_BROADCAST_SEND },
  SMS_BROADCAST_VIEW: { admin: PERMISSION_KEYS.SMS_BROADCAST_VIEW },
  SMS_BROADCAST_SEND: { admin: PERMISSION_KEYS.SMS_BROADCAST_SEND },
  COMPANIES_VIEW: { admin: PERMISSION_KEYS.COMPANIES_VIEW },
  COMPANIES_CREATE: { admin: PERMISSION_KEYS.COMPANIES_CREATE },
  COMPANIES_UPDATE: { admin: PERMISSION_KEYS.COMPANIES_UPDATE },
  COMPANIES_DELETE: { admin: PERMISSION_KEYS.COMPANIES_DELETE },
  // Sistem Ayarları — sertifika numarası pattern'i vb. alan-geneli ayarlar (sadece VIEW + UPDATE)
  DOMAIN_SETTINGS_VIEW: { admin: PERMISSION_KEYS.DOMAIN_SETTINGS_VIEW },
  DOMAIN_SETTINGS_UPDATE: { admin: PERMISSION_KEYS.DOMAIN_SETTINGS_UPDATE },
} as const;
